import type {
  ITopFiveApiV2EndpointsStaffSharedModelsStaffItemDto,
  ITopFiveApiV2EndpointsStaffGetStaffByIdGetStaffByIdResponse,
} from '@/api/models';
import {
  useITopFiveApiV2EndpointsStaffGetStaffByIdGetStaffByIdEndpoint,
  getITopFiveApiV2EndpointsStaffGetStaffByIdGetStaffByIdEndpointQueryKey,
} from '@/api/api.generated';

/**
 * Query key factory for staff by id endpoint
 */
export const staffByIdQueryKey = (staffId: number) =>
  getITopFiveApiV2EndpointsStaffGetStaffByIdGetStaffByIdEndpointQueryKey(staffId);

/**
 * Response type for useStaffById hook
 */
export interface UseStaffByIdResult {
  /** Staff detail data */
  staffDetail: ITopFiveApiV2EndpointsStaffSharedModelsStaffItemDto | null;
  /** Whether user has iNominate admin access */
  hasAdminAccess: boolean;
  /** Whether user has iTopFive staff editor access */
  hasEditorAccess: boolean;
  /** Whether the query is loading */
  isLoading: boolean;
  /** Whether there was an error */
  isError: boolean;
  /** Error object if any */
  error: Error | null;
  /** Function to refetch the data */
  refetch: () => void;
}

/**
 * Custom hook to fetch a staff member by ID from the API
 *
 * Uses React Query for caching and state management.
 *
 * @param staffId - The ID of the staff member to fetch
 * @param enabled - Whether the query should be enabled (default: true)
 *
 * @example
 * ```tsx
 * const { staffDetail, isLoading, hasEditorAccess } = useStaffById(1);
 *
 * if (isLoading) return <Spinner />;
 *
 * return <StaffForm defaultValues={staffDetail} />;
 * ```
 */
export const useStaffById = (
  staffId: number,
  enabled: boolean = true
): UseStaffByIdResult => {
  const { data, isLoading, isError, error, refetch } =
    useITopFiveApiV2EndpointsStaffGetStaffByIdGetStaffByIdEndpoint(staffId, {
      query: { enabled: enabled && staffId > 0 },
      axios: { withCredentials: true },
    });

  return {
    staffDetail: data?.data?.staffDetail ?? null,
    hasAdminAccess: data?.data?.hasiNominateAccess ?? false,
    hasEditorAccess: data?.data?.hasiTopFiveStaffEditorAccess ?? false,
    isLoading,
    isError,
    error: error as Error | null,
    refetch,
  };
};

export default useStaffById;
